import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Fab,
  useMediaQuery,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Badge,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { User } from "../services/api";
import PageContainer from "../components/PageContainer";
import PageSection from "../components/PageSection";
import { UserList } from "../components/UserManagement";
import UsersStatsBar from "../components/UserManagement/UsersStatsBar";
import UsersFilterBar from "../components/UserManagement/UsersFilterBar";
import { userHomeHref } from "../routes";
import InvitationList from "../components/UserManagement/InvitationList";
import JoinRequestList, {
  JoinRequest,
} from "../components/UserManagement/JoinRequestList";
import AddUserFromSearchEmptyState from "../components/UserManagement/AddUserFromSearchEmptyState";
import InviteUsers from "../components/InviteUsers";
import {
  DeleteModal,
  NoticeboardButton,
  EmptyState,
  TabPanel,
} from "../components/shared";
import { useUsers } from "../hooks/useUsers";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import { useInvitations } from "../hooks/useInvitations";
import { useJoinRequests } from "../hooks/useJoinRequests";
import { useDismissJoinRequest } from "../hooks/useDismissJoinRequest";
import { useCancelInvitation } from "../hooks/useCancelInvitation";
import { useUpdateInvitationAlias } from "../hooks/useUpdateInvitationAlias";
import { useRegisterPageActions } from "../context/PageActionsContext";
import UserManagementSkeleton from "../components/UserManagement/UserManagementSkeleton";
import {
  MANAGEMENT_ADD_MORE_BUTTON_SX,
  MANAGEMENT_MOBILE_FAB_SX,
  mobileFabWithScrollVisibility,
} from "../styles/managementShared";
import { useMobileFabVisibility } from "../hooks/useMobileFabVisibility";
import VideoPlayerModal from "../components/VideoPlayerModal";
import {
  isRealUserEmail,
  parseUserSearchContact,
} from "../utils/userContactUtils";
import { phoneMatchesSearch } from "../utils/phoneUtils";
import { useFamilySearchIndex } from "../hooks/useFamilySearchIndex";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { useUserListFilterState } from "../hooks/useUserListFilterState";
import { useUserAliasFilters } from "../hooks/useUserAliasFilters";
import { useFilteredUsers } from "../hooks/useFilteredUsers";
import { filterActionableJoinRequests } from "../utils/joinRequestUtils";
import { useAvailableInviteCircles } from "../hooks/useAvailableInviteCircles";
import {
  readUsersHandover,
  type InvitePrefill,
} from "../domain/users/usersHandover";

const ManageUsers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState<InvitePrefill | null>(
    null
  );
  const [searchSelectedCircleIds, setSearchSelectedCircleIds] = useState<
    string[]
  >([]);
  const [sourceJoinRequestId, setSourceJoinRequestId] = useState<string | null>(
    null
  );
  const filterState = useUserListFilterState();
  const {
    searchQuery,
    setSearchQuery,
    activeFilter,
    selectedSubcircleId,
    aliasFilter,
    setAliasFilter,
    subcircleMenuAnchor,
    setSubcircleMenuAnchor,
    aliasFilterMenuAnchor,
    setAliasFilterMenuAnchor,
    isNoSubcirclesFilter,
    activeSubcircleId,
    hasUserListFilters,
    handleFilterClick,
    handleSubcircleSelect,
    handleClearUserFilters,
  } = filterState;
  const [activeTab, setActiveTab] = useState(0);
  const tabFromQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabFromQuery === "requests") {
      setActiveTab(2);
    } else if (tabFromQuery === "invitations") {
      setActiveTab(1);
    }
  }, [tabFromQuery]);

  // Another page (Ask Noticeboard) can open Users searched, or with an Invite filled in.
  // Read once, then cleared, so a reload or Back never opens it again.
  useEffect(() => {
    const { search, invite } = readUsersHandover(location.state);
    if (!search && !invite) return;
    if (search) setSearchQuery(search);
    if (invite) {
      setInvitePrefill(invite);
      setSourceJoinRequestId(null);
      setInviteOpen(true);
    }
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.state, location.pathname, location.search, navigate, setSearchQuery]);
  const [invitationToCancel, setInvitationToCancel] = useState<{
    invitationId: string;
    contact: string;
  } | null>(null);
  const [cancelConfirmationText, setCancelConfirmationText] = useState("");
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [invitationActionError, setInvitationActionError] = useState<
    string | null
  >(null);
  const [joinRequestActionError, setJoinRequestActionError] = useState<
    string | null
  >(null);
  const [requestToDismiss, setRequestToDismiss] = useState<JoinRequest | null>(
    null
  );
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const { user: authUser, token } = useAuth();
  const { isFamilyManagementEnabled } = useFeatureFlags();
  const { availableCircles, isLoading: isCirclesLoading } =
    useAvailableInviteCircles();
  const { data: users = [], isLoading, refetch, error } = useUsers();
  const { data: familySearchIndex = [] } = useFamilySearchIndex(
    isFamilyManagementEnabled
  );
  const {
    data: invitations = [],
    isLoading: isLoadingInvitations,
    refetch: refetchInvitations,
  } = useInvitations();
  const {
    data: joinRequests = [],
    isLoading: isLoadingJoinRequests,
    refetch: refetchJoinRequests,
  } = useJoinRequests();

  const {
    subcircles,
    subcircleMemberIds,
    allSubcircleMemberIds,
    aliasByUserId,
    isAliasDataLoading,
    isUserListLoading: isAliasFilterLoading,
    isLoadingSubcircleMembers,
    subcircleMembersError,
    selectedSubcircleName,
  } = useUserAliasFilters({
    selectedSubcircleId,
    isNoSubcirclesFilter,
    activeSubcircleId,
  });

  const { mutate: cancelInvitation, isPending: isCancelingInvitation } =
    useCancelInvitation();
  const {
    mutateAsync: updateInvitationAlias,
    isPending: isUpdatingInvitationAlias,
    variables: updatingAliasVariables,
  } = useUpdateInvitationAlias();
  const { mutate: dismissJoinRequest, isPending: isDismissingJoinRequest } =
    useDismissJoinRequest();
  const isMdUp = useMediaQuery("(min-width:900px)");
  const isMobile = useMediaQuery("(max-width:599px)");
  const mobileFabVisible = useMobileFabVisibility(isMobile);

  const hasSearchQuery = searchQuery.trim() !== "";

  const searchPlaceholder =
    activeTab === 0
      ? "Search by name, email, phone, alias, or child..."
      : activeTab === 1
        ? "Search invitations by email, phone, or alias..."
        : "Search requests by learner, parent, email, or phone...";

  const filteredUsers = useFilteredUsers({
    users,
    searchQuery,
    activeFilter,
    isNoSubcirclesFilter,
    isAliasDataLoading,
    allSubcircleMemberIds,
    subcircleMemberIds,
    aliasByUserId,
    aliasFilter,
    familySearchIndex,
    familySearchEnabled: isFamilyManagementEnabled,
  });

  const isUserListLoading =
    isLoading || isAliasFilterLoading;

  // Memoized filtered invitations with search logic
  const filteredInvitations = useMemo(() => {
    let filtered = invitations;

    // Apply search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (invitation) =>
          (invitation.email && invitation.email.toLowerCase().includes(q)) ||
          phoneMatchesSearch(invitation.phoneNumber, searchQuery) ||
          (invitation.memberAlias &&
            invitation.memberAlias.toLowerCase().includes(q))
      );
    }

    return filtered;
  }, [invitations, searchQuery]);

  const filteredJoinRequests = useMemo(
    () => filterActionableJoinRequests(joinRequests, users, searchQuery),
    [joinRequests, searchQuery, users]
  );

  useEffect(() => {
    setSearchSelectedCircleIds([]);
  }, [searchQuery]);

  const toggleSearchCircleSelection = useCallback((circleId: string) => {
    setSearchSelectedCircleIds((prev) =>
      prev.includes(circleId)
        ? prev.filter((id) => id !== circleId)
        : [...prev, circleId]
    );
  }, []);

  const usersTabCount = filteredUsers.length;
  const invitationsTabCount = filteredInvitations.length;
  const joinRequestsTabCount = filteredJoinRequests.length;

  const addUserFromSearchPrefill = useMemo(() => {
    const parsed = parseUserSearchContact(searchQuery);
    if (!parsed || !hasSearchQuery) return null;
    if (
      usersTabCount > 0 ||
      invitationsTabCount > 0 ||
      joinRequestsTabCount > 0
    ) {
      return null;
    }
    return parsed;
  }, [
    searchQuery,
    hasSearchQuery,
    usersTabCount,
    invitationsTabCount,
    joinRequestsTabCount,
  ]);

  const isAddFromSearchReady =
    !!addUserFromSearchPrefill &&
    !isLoadingInvitations &&
    !isLoadingJoinRequests &&
    !isUserListLoading &&
    !(activeSubcircleId && isLoadingSubcircleMembers) &&
    !(isNoSubcirclesFilter && isAliasDataLoading) &&
    !(aliasFilter !== "all" && isAliasDataLoading);

  // Memoized handlers
  const handleUserClick = useCallback(
    (targetUser: User) => {
      navigate(userHomeHref(targetUser.id));
    },
    [navigate]
  );

  const handleOpenInvite = useCallback(() => {
    setInvitePrefill(null);
    setSourceJoinRequestId(null);
    setInviteOpen(true);
  }, []);

  const handleOpenInviteFromRequest = useCallback((request: JoinRequest) => {
    const learnerName =
      `${request.learnerName} ${request.learnerSurname}`.trim();
    setInvitePrefill({
      email: request.email?.trim() || undefined,
      phoneNumber: request.cellNumber?.trim() || undefined,
      alias: learnerName || request.parentName?.trim() || undefined,
    });
    setSourceJoinRequestId(request.requestId);
    setInviteOpen(true);
  }, []);

  const handleOpenInviteFromSearch = useCallback(() => {
    if (!addUserFromSearchPrefill) return;
    setInvitePrefill({
      ...addUserFromSearchPrefill,
      circleIds:
        searchSelectedCircleIds.length > 0
          ? searchSelectedCircleIds
          : undefined,
    });
    setSourceJoinRequestId(null);
    setInviteOpen(true);
  }, [addUserFromSearchPrefill, searchSelectedCircleIds]);

  const addUserFromSearchPanel = isAddFromSearchReady ? (
    <AddUserFromSearchEmptyState
      contactLabel={searchQuery.trim()}
      circles={availableCircles}
      selectedCircleIds={searchSelectedCircleIds}
      onToggleCircle={toggleSearchCircleSelection}
      circlesLoading={isCirclesLoading}
      onAddUser={handleOpenInviteFromSearch}
    />
  ) : null;

  const handleDismissRequestClick = useCallback((request: JoinRequest) => {
    setJoinRequestActionError(null);
    setRequestToDismiss(request);
  }, []);

  const handleCloseDismissModal = useCallback(() => {
    setRequestToDismiss(null);
  }, []);

  const handleConfirmDismissRequest = useCallback(() => {
    if (!requestToDismiss) return;

    dismissJoinRequest(requestToDismiss.requestId, {
      onSuccess: () => {
        setRequestToDismiss(null);
        setSuccessMessage("Join request dismissed");
        setTimeout(() => setSuccessMessage(null), 5000);
      },
      onError: () => {
        setJoinRequestActionError(
          "Failed to dismiss join request. Please try again."
        );
      },
    });
  }, [dismissJoinRequest, requestToDismiss]);

  const handleCloseInvite = useCallback(() => {
    setInviteOpen(false);
    setInvitePrefill(null);
    setSourceJoinRequestId(null);
  }, []);

  const handleCancelInvitationClick = useCallback(
    (invitationId: string) => {
      const invitation = invitations.find(
        (inv) => inv.invitationId === invitationId
      );
      if (invitation) {
        const contact =
          invitation.email || invitation.phoneNumber || "this invitation";
        setInvitationActionError(null);
        setInvitationToCancel({ invitationId, contact });
        setCancelConfirmationText("");
      }
    },
    [invitations]
  );

  const handleCloseCancelModal = useCallback(() => {
    setInvitationToCancel(null);
    setCancelConfirmationText("");
    setInvitationActionError(null);
  }, []);

  const handleConfirmCancelInvitation = useCallback(() => {
    if (!invitationToCancel) return;

    cancelInvitation(invitationToCancel.invitationId, {
      onSuccess: () => {
        setInvitationToCancel(null);
        setCancelConfirmationText("");
        setInvitationActionError(null);
        refetchInvitations();
      },
      onError: (error) => {
        console.error("Error canceling invitation:", error);
        setInvitationActionError(
          "Could not cancel the invitation. Please try again."
        );
      },
    });
  }, [cancelInvitation, invitationToCancel, refetchInvitations]);

  const handleInvitationAliasChange = useCallback(
    async (invitationId: string, alias: string) => {
      setInvitationActionError(null);
      try {
        await updateInvitationAlias({ invitationId, alias });
      } catch (error) {
        console.error("Error updating invitation alias:", error);
        setInvitationActionError(
          "Could not save the alias. Please try again."
        );
        throw error;
      }
    },
    [updateInvitationAlias]
  );

  const pageActions = useMemo(
    () => ({
      onMainAction: handleOpenInvite,
      onVideoPlay: () =>
        setSelectedVideo({
          videoPath: "/videos/adduser.webm",
          title: "How to Add Users",
        }),
      count: users.length,
    }),
    [handleOpenInvite, users.length]
  );

  useRegisterPageActions(pageActions);

  const userStats = useMemo(() => {
    const totalUsers = users.length;
    const mobileUsers = users.filter(
      (user) => user.phoneNumber && user.phoneNumber.trim() !== ""
    ).length;
    const emailUsers = users.filter((user) =>
      isRealUserEmail(user.email)
    ).length;

    return {
      totalUsers,
      mobileUsers,
      emailUsers,
    };
  }, [users]);

  if (isLoading) {
    return <UserManagementSkeleton />;
  }

  const content = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users. Please try refreshing the page.
        </Alert>
      )}

      {subcircleMembersError && activeSubcircleId && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load subcircle members. Please try again.
        </Alert>
      )}

      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {joinRequestActionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJoinRequestActionError(null)}>
          {joinRequestActionError}
        </Alert>
      )}

      {invitationActionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setInvitationActionError(null)}
        >
          {invitationActionError}
        </Alert>
      )}

      {users.length > 0 ? (
        <UsersStatsBar
          totalUsers={userStats.totalUsers}
          mobileUsers={userStats.mobileUsers}
          emailUsers={userStats.emailUsers}
          activeFilter={activeFilter}
          onFilterClick={handleFilterClick}
        />
      ) : null}

      {users.length === 0 &&
            invitations.length === 0 &&
            joinRequests.length === 0 &&
            !isLoading &&
            !isLoadingInvitations &&
            !isLoadingJoinRequests ? (
              <EmptyState
                title="Add Your First User"
                description="Invite users to join your organization. They'll be automatically added to your organization when they sign up if they don't have an account yet."
                imageSrc="/users/user.webp"
                imageAlt="Users illustration"
                buttonText="Invite your first user"
                onButtonClick={handleOpenInvite}
                benefits={[
                  "Connect parents and community members",
                  "Enable user access to circles",
                  "Manage user permissions",
                  "Track user engagement",
                ]}
                benefitsTitle="Why add users?"
                onBenefitsTitleVideoClick={() =>
                  setSelectedVideo({
                    videoPath: "/videos/adduser.webm",
                    title: "Why Add Users?",
                  })
                }
                howItWorks={[
                  "Invite users via email or phone number",
                  "Automatically added to your organization when they sign up",
                  "Grant access to specific circles",
                ]}
                howItWorksTitle="How it works:"
              />
            ) : (
              <Paper
                elevation={0}
                sx={{
                  border: { xs: "none", md: "1px solid" },
                  borderColor: { md: "divider" },
                  borderRadius: { xs: 0, md: 2 },
                  overflow: { xs: "visible", md: "hidden" },
                  backgroundColor: { xs: "transparent", md: "background.paper" },
                }}
              >
                {(users.length > 0 ||
                  invitations.length > 0 ||
                  joinRequests.length > 0) && (
                  <Box
                    sx={{
                      px: { xs: 0, sm: 3 },
                      pt: { xs: 1.5, sm: 2 },
                      pb: { xs: 1, sm: 1.5 },
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <UsersFilterBar
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      searchPlaceholder={searchPlaceholder}
                      showSubcircleFilters={
                        activeTab === 0 && users.length > 0 && subcircles.length > 0
                      }
                      selectedSubcircleId={selectedSubcircleId}
                      selectedSubcircleName={selectedSubcircleName}
                      subcircleMenuAnchor={subcircleMenuAnchor}
                      onSubcircleMenuOpen={setSubcircleMenuAnchor}
                      onSubcircleMenuClose={() => setSubcircleMenuAnchor(null)}
                      onSubcircleSelect={handleSubcircleSelect}
                      subcircles={subcircles}
                      isNoSubcirclesFilter={isNoSubcirclesFilter}
                      aliasFilter={aliasFilter}
                      aliasFilterMenuAnchor={aliasFilterMenuAnchor}
                      onAliasFilterMenuOpen={setAliasFilterMenuAnchor}
                      onAliasFilterMenuClose={() => setAliasFilterMenuAnchor(null)}
                      onAliasFilterSelect={setAliasFilter}
                      hasUserListFilters={hasUserListFilters}
                      onClearFilters={handleClearUserFilters}
                      showUserResultCount
                      userResultCount={filteredUsers.length}
                      isUserListLoading={isUserListLoading}
                      isAliasDataLoading={isAliasDataLoading}
                      showInvitationResultCount={hasSearchQuery}
                      invitationResultCount={filteredInvitations.length}
                      isLoadingInvitations={isLoadingInvitations}
                      showJoinRequestResultCount={hasSearchQuery}
                      joinRequestResultCount={filteredJoinRequests.length}
                      isLoadingJoinRequests={isLoadingJoinRequests}
                      activeTab={activeTab}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    borderRadius: { xs: 2, md: 0 },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="users, invitations, and requests tabs"
                    variant={isMobile ? "fullWidth" : isMdUp ? "standard" : "scrollable"}
                    allowScrollButtonsMobile={!isMobile}
                    scrollButtons={isMobile ? false : isMdUp ? false : "auto"}
                    sx={{
                      minHeight: { xs: 44, sm: 48 },
                      "& .MuiTab-root": {
                        minHeight: { xs: 44, sm: 48 },
                        px: { xs: 0.75, sm: 2 },
                        fontSize: { xs: "0.76rem", sm: "0.875rem" },
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <Tab
                      label={
                        isMobile
                          ? `Users (${usersTabCount})`
                          : `Active Users (${usersTabCount})`
                      }
                      id="users-tab-0"
                      aria-controls="users-tabpanel-0"
                    />
                    <Tab
                      label={
                        isMobile
                          ? `Invites (${invitationsTabCount})`
                          : `Pending Invitations (${invitationsTabCount})`
                      }
                      id="users-tab-1"
                      aria-controls="users-tabpanel-1"
                    />
                    <Tab
                      label={
                        <Badge
                          color="error"
                          variant="dot"
                          invisible={joinRequestsTabCount === 0}
                          sx={{
                            "& .MuiBadge-badge": {
                              top: 4,
                              right: -6,
                            },
                          }}
                        >
                          {`Requests (${joinRequestsTabCount})`}
                        </Badge>
                      }
                      id="users-tab-2"
                      aria-controls="users-tabpanel-2"
                    />
                  </Tabs>
                </Box>

                {/* Active Users Tab */}
                <TabPanel idPrefix="users" value={activeTab} index={0}>
                  <Box sx={{ px: { xs: 0, sm: 3 }, pb: { xs: 1.5, sm: 3 } }}>
                    {addUserFromSearchPanel ??
                    (activeSubcircleId && isLoadingSubcircleMembers ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 4, textAlign: "center" }}
                      >
                        Loading subcircle members…
                      </Typography>
                    ) : isNoSubcirclesFilter && isAliasDataLoading ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 4, textAlign: "center" }}
                      >
                        Finding users without subcircles…
                      </Typography>
                    ) : aliasFilter !== "all" && isAliasDataLoading ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 4, textAlign: "center" }}
                      >
                        Loading alias data…
                      </Typography>
                    ) : (
                      <UserList
                        users={filteredUsers}
                        onUserClick={handleUserClick}
                        isMobile={!isMdUp}
                        isLoading={isUserListLoading}
                        emptyTitle={
                          hasUserListFilters
                            ? "No users match your filters"
                            : undefined
                        }
                        emptyDescription={
                          hasUserListFilters
                            ? "Try adjusting your search or filters."
                            : undefined
                        }
                      />
                    ))}
                    {filteredUsers.length > 0 && !isMobile && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 2,
                          pt: 2,
                          borderTop: "1px dashed",
                          borderColor: "divider",
                        }}
                      >
                        <Button
                          variant="contained"
                          onClick={handleOpenInvite}
                          sx={MANAGEMENT_ADD_MORE_BUTTON_SX}
                        >
                          + Add More Users
                        </Button>
                      </Box>
                    )}
                  </Box>
                </TabPanel>

                {/* Pending Invitations Tab */}
                <TabPanel idPrefix="users" value={activeTab} index={1}>
                  <Box sx={{ px: { xs: 0, sm: 3 }, pb: { xs: 1.5, sm: 3 } }}>
                    {addUserFromSearchPanel ?? (
                    <InvitationList
                      invitations={filteredInvitations}
                      isMobile={!isMdUp}
                      isLoading={isLoadingInvitations}
                      onCancelInvitation={handleCancelInvitationClick}
                      onAliasChange={handleInvitationAliasChange}
                      savingAliasInvitationId={
                        isUpdatingInvitationAlias
                          ? updatingAliasVariables?.invitationId ?? null
                          : null
                      }
                      isCanceling={isCancelingInvitation}
                      emptyTitle={
                        hasSearchQuery
                          ? "No invitations match your search"
                          : undefined
                      }
                      emptyDescription={
                        hasSearchQuery
                          ? "Try a different email, phone number, or alias."
                          : undefined
                      }
                    />
                    )}
                  </Box>
                </TabPanel>

                {/* Join Requests Tab */}
                <TabPanel idPrefix="users" value={activeTab} index={2}>
                  <Box sx={{ px: { xs: 0, sm: 3 }, pb: { xs: 1.5, sm: 3 } }}>
                    {addUserFromSearchPanel ?? (
                    <JoinRequestList
                      joinRequests={filteredJoinRequests}
                      isMobile={!isMdUp}
                      isLoading={isLoadingJoinRequests}
                      onAddUser={handleOpenInviteFromRequest}
                      onDismiss={handleDismissRequestClick}
                      emptyTitle={
                        hasSearchQuery
                          ? "No requests match your search"
                          : undefined
                      }
                      emptyDescription={
                        hasSearchQuery
                          ? "Try a different name, email, or phone number."
                          : undefined
                      }
                    />
                    )}
                  </Box>
                </TabPanel>
              </Paper>
            )}

      <InviteUsers
        hideHeader
        initialInvite={invitePrefill ?? undefined}
        actionModalProps={{
          open: inviteOpen,
          onClose: handleCloseInvite,
        }}
        onSuccess={async (message) => {
          if (message) {
            setSuccessMessage(message);
            // Clear success message after 5 seconds
            setTimeout(() => setSuccessMessage(null), 5000);
          }

          if (sourceJoinRequestId && authUser?.schoolId && token) {
            try {
              await api.users.acceptJoinRequest(
                authUser.schoolId,
                sourceJoinRequestId,
                token
              );
              await refetchJoinRequests();
            } catch (joinRequestError) {
              console.error(
                "Failed to mark join request as accepted:",
                joinRequestError
              );
            }
          }

          refetch();
          refetchInvitations();
          handleCloseInvite();
        }}
      />

      {/* Dismiss Join Request Confirmation Modal */}
      <DeleteModal
        open={!!requestToDismiss}
        onClose={handleCloseDismissModal}
        title="Dismiss Join Request"
        subtitle="This request will be removed from your list"
      >
        <Typography
          variant="body1"
          sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}
        >
          Dismiss the join request for{" "}
          <Typography component="span" fontWeight={600}>
            {requestToDismiss
              ? `${requestToDismiss.learnerName} ${requestToDismiss.learnerSurname}`.trim()
              : ""}
          </Typography>
          ? They will not be added automatically. You can still invite them manually
          later if needed.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <NoticeboardButton variant="secondary" onClick={handleCloseDismissModal}>
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="danger"
            onClick={handleConfirmDismissRequest}
            disabled={isDismissingJoinRequest}
          >
            {isDismissingJoinRequest ? "Dismissing..." : "Dismiss Request"}
          </NoticeboardButton>
        </Box>
      </DeleteModal>

      {/* Cancel Invitation Confirmation Modal */}
      <DeleteModal
        open={!!invitationToCancel}
        onClose={handleCloseCancelModal}
        title="Cancel Invitation"
        subtitle="This action cannot be undone"
      >
        <Typography
          variant="body1"
          sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}
        >
          Are you sure you want to cancel the invitation for{" "}
          <Typography component="span" fontWeight={600}>
            {invitationToCancel?.contact}
          </Typography>
          ? This action cannot be undone and the invitation will be permanently
          removed.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            To confirm cancellation, please type the contact:{" "}
            <Typography component="span" fontWeight={600} color="error.main">
              {invitationToCancel?.contact}
            </Typography>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type the contact to confirm"
            value={cancelConfirmationText}
            onChange={(e) => setCancelConfirmationText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <NoticeboardButton
            variant="danger"
            onClick={handleConfirmCancelInvitation}
            disabled={
              isCancelingInvitation ||
              cancelConfirmationText !== invitationToCancel?.contact
            }
          >
            {isCancelingInvitation ? "Canceling..." : "Cancel Invitation"}
          </NoticeboardButton>
        </Box>
      </DeleteModal>
      {isMobile ? (
        <Fab
          color="primary"
          aria-label="Add users"
          onClick={handleOpenInvite}
          sx={mobileFabWithScrollVisibility(MANAGEMENT_MOBILE_FAB_SX, mobileFabVisible)}
        >
          <AddIcon sx={{ fontSize: 30 }} />
        </Fab>
      ) : null}
    </>
  );

  return (
    <PageContainer sx={{ mt: 2 }}>
      <PageSection sx={{ mb: 2 }}>{content}</PageSection>
      <VideoPlayerModal
        open={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        videoPath={selectedVideo?.videoPath || ""}
        title={selectedVideo?.title || ""}
      />
    </PageContainer>
  );
};

export default ManageUsers;
